import { useState } from 'react';
import { Edit2, Calendar, Clock, User, GripVertical, Check } from 'lucide-react';
import Checkbox from '../Checkbox';
import { TASK_STATUSES, TIME_ESTIMATES, computeStatus } from '../../hooks/useTasks';

function getTimeEstimateLabel(timeEstimate) {
  const estimate = TIME_ESTIMATES.find(t => t.id === timeEstimate);
  return estimate ? estimate.label : '15m';
}

export default function TimeBlockTaskItem({
  task,
  onToggleComplete,
  onEdit,
  onReschedule,
  onDelegate,
  availableBlocks = [],
  draggable = false,
  onDragStart,
  onDragEnd
}) {
  const [showReschedule, setShowReschedule] = useState(false);
  const isDone = computeStatus(task) === TASK_STATUSES.DONE;

  const handleReschedule = (blockId, blockName, blockDate) => {
    onReschedule(task.id, blockId, blockName, blockDate);
    setShowReschedule(false);
  };

  return (
    <div
      className={`
        group flex items-start gap-2 p-2.5 rounded-lg
        bg-surface-card/60 hover:bg-surface-card
        border border-border hover:border-border-strong
        transition-all duration-200
        ${isDone ? 'opacity-50' : ''}
        ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}
      `}
      draggable={draggable}
      onDragStart={(e) => {
        if (draggable && onDragStart) {
          e.dataTransfer.setData('taskId', task.id);
          onDragStart(task);
        }
      }}
      onDragEnd={onDragEnd}
    >
      {/* Drag handle */}
      {draggable && (
        <div className="flex items-center text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical size={14} />
        </div>
      )}

      {/* Checkbox */}
      <div className="pt-0.5">
        <Checkbox
          checked={isDone}
          onChange={() => onToggleComplete(task.id)}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm text-text-primary ${isDone ? 'line-through' : ''}`}>
          {task.title}
        </p>

        {/* Meta row */}
        <div className="flex items-center gap-2 mt-1">
          {/* Time estimate */}
          <span className="text-[10px] text-text-tertiary flex items-center gap-0.5">
            <Clock size={10} />
            {getTimeEstimateLabel(task.timeEstimate)}
          </span>

          {/* Delegated to */}
          {task.delegatedTo && (
            <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
              <User size={10} />
              {task.delegatedTo}
            </span>
          )}

          {/* Completed indicator */}
          {isDone && (
            <span className="text-[10px] text-success flex items-center gap-0.5">
              <Check size={10} />
              Done
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Edit */}
        <button
          onClick={() => onEdit(task)}
          className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-surface-hover hover:text-text-secondary transition-all"
          title="Edit task"
        >
          <Edit2 size={12} />
        </button>

        {/* Reschedule */}
        <div className="relative">
          <button
            onClick={() => setShowReschedule(!showReschedule)}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-surface-hover hover:text-text-secondary transition-all"
            title="Reschedule to different block"
          >
            <Calendar size={12} />
          </button>

          {/* Reschedule dropdown */}
          {showReschedule && availableBlocks.length > 0 && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-surface-elevated border border-border rounded-lg shadow-lg z-50 py-1">
              <div className="px-2 py-1 text-[10px] text-text-muted font-medium">
                Move to block:
              </div>
              {availableBlocks.map((block) => (
                <button
                  key={block.id}
                  onClick={() => handleReschedule(block.id, block.summary, block.date)}
                  className="w-full px-2 py-1.5 text-left text-xs text-text-primary hover:bg-surface-hover cursor-pointer bg-transparent border-none"
                >
                  {block.summary}
                  <span className="text-text-tertiary ml-1">{block.timeRange}</span>
                </button>
              ))}
              <div className="border-t border-border my-1" />
              <button
                onClick={() => {
                  onReschedule(task.id, null, null, null);
                  setShowReschedule(false);
                }}
                className="w-full px-2 py-1.5 text-left text-xs text-red-400 hover:bg-red-500/10 cursor-pointer bg-transparent border-none"
              >
                Remove from block
              </button>
            </div>
          )}
        </div>

        {/* Delegate */}
        {onDelegate && (
          <button
            onClick={() => onDelegate(task)}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-surface-hover hover:text-amber-400 transition-all"
            title="Delegate task"
          >
            <User size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
